import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';

export default function VerifyEmailPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'expired'>('verifying');
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');
    const [resending, setResending] = useState(false);
    const [resendMessage, setResendMessage] = useState('');

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            setStatus('error');
            setMessage('Token de verificação não fornecido.');
            return;
        }

        verifyEmail(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const verifyEmail = async (token: string) => {
        try {
            const response = await apiClient.get(`/auth/verify-email?token=${token}`);

            if (response.data.alreadyVerified) {
                setStatus('success');
                setMessage('Seu e-mail já foi verificado anteriormente. Redirecionando para sua conta...');
            } else {
                setStatus('success');
                setMessage(response.data.message || 'E-mail verificado com sucesso! Redirecionando...');
            }

            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (error: any) {
            // Verificar se o token já foi usado (email já verificado)
            if (error.response?.data?.alreadyUsed) {
                setStatus('success');
                setMessage('Seu e-mail já foi verificado! Redirecionando para o login...');
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else if (error.response?.data?.tokenExpired) {
                setStatus('expired');
                setMessage(error.response.data.error);
            } else {
                setStatus('error');
                setMessage(error.response?.data?.error || 'Erro ao verificar e-mail.');
            }
        }
    };

    const handleResendVerification = async () => {
        if (!email) {
            setResendMessage('Por favor, insira seu e-mail.');
            return;
        }

        setResending(true);
        setResendMessage('');

        try {
            const response = await apiClient.post('/auth/resend-verification', { email });
            setResendMessage(response.data.message);
        } catch (error: any) {
            // Verificar se é rate limit (429)
            if (error.response?.status === 429 && error.response?.data?.rateLimited) {
                const message = error.response.data.message || 'Verifique sua caixa de entrada e também a pasta de spam/lixo eletrônico.';
                setResendMessage(`⏰ ${error.response.data.error}\n\n📧 ${message}`);
            } else {
                setResendMessage(error.response?.data?.error || 'Erro ao reenviar e-mail de verificação.');
            }
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 py-12">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
                {status === 'verifying' && (
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verificando seu e-mail...</h2>
                        <p className="text-gray-600">Por favor, aguarde um momento.</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="text-center">
                        <svg className="h-16 w-16 text-green-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">E-mail Verificado!</h2>
                        <p className="text-gray-600 mb-4">{message}</p>
                        <div className="flex items-center justify-center text-sm text-gray-500">
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Redirecionando para o login...
                        </div>
                    </div>
                )}

                {status === 'error' && (
                    <div className="text-center">
                        <svg className="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Erro na Verificação</h2>
                        <p className="text-gray-600 mb-6">{message}</p>
                        <button
                            onClick={() => navigate('/login')}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Ir para o Login
                        </button>
                    </div>
                )}

                {status === 'expired' && (
                    <div className="text-center">
                        <svg className="h-16 w-16 text-orange-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Link Expirado</h2>
                        <p className="text-gray-600 mb-6">{message}</p>

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                    Digite seu e-mail para receber um novo link:
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="seu@email.com"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <button
                                onClick={handleResendVerification}
                                disabled={resending}
                                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {resending ? 'Enviando...' : 'Reenviar Link de Verificação'}
                            </button>

                            {resendMessage && (
                                <div className={`p-4 rounded-lg text-sm ${
                                    resendMessage.includes('Erro') || resendMessage.includes('já está verificado')
                                        ? 'bg-red-50 text-red-700 border border-red-200'
                                        : resendMessage.includes('⏰')
                                        ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                                        : 'bg-green-50 text-green-700 border border-green-200'
                                }`}>
                                    {resendMessage.split('\n\n').map((line, index) => (
                                        <p key={index} className={index > 0 ? 'mt-2 font-medium' : ''}>
                                            {line}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


